const SESSION_TRANSITION_LOCK_NAME = "imprint-reader-session-transition-v1";

type SessionIntentResult<T> = { committed: true; value: T } | { committed: false };

export type SessionTransitionLock = <T>(operation: () => Promise<T>) => Promise<T>;

export type SessionIntent = Readonly<{
  kind: "observation" | "transition";
  id: number;
  transitionEpoch: number;
  beganDuringTransition: boolean;
}>;

export interface SessionIntentCoordinator {
  beginObservation: () => SessionIntent;
  beginTransition: () => SessionIntent;
  isCurrent: (intent: SessionIntent) => boolean;
  observe: <T>(intent: SessionIntent, operation: () => Promise<T>) => Promise<SessionIntentResult<T>>;
  transition: <T>(intent: SessionIntent, operation: () => Promise<T>) => Promise<SessionIntentResult<T>>;
}

export function createSessionIntentCoordinator({
  withTransitionLock = withSameOriginSessionTransitionLock,
}: {
  withTransitionLock?: SessionTransitionLock;
} = {}): SessionIntentCoordinator {
  let nextIntentId = 0;
  let transitionEpoch = 0;
  let latestObservationId = 0;
  let latestTransitionId = 0;
  const pendingTransitions = new Set<number>();
  let operationTail = Promise.resolve();

  function observationIsCurrent(intent: SessionIntent): boolean {
    return intent.kind === "observation"
      && !intent.beganDuringTransition
      && intent.id === latestObservationId
      && intent.transitionEpoch === transitionEpoch
      && pendingTransitions.size === 0;
  }

  function transitionIsCurrent(intent: SessionIntent): boolean {
    return intent.kind === "transition" && intent.id === latestTransitionId;
  }

  function observe<T>(intent: SessionIntent, operation: () => Promise<T>): Promise<SessionIntentResult<T>> {
    const guardedOperation = async (): Promise<SessionIntentResult<T>> => {
      if (!observationIsCurrent(intent)) return { committed: false };
      const value = await operation();
      if (!observationIsCurrent(intent)) return { committed: false };
      return { committed: true, value };
    };
    const result = operationTail.then(() => withTransitionLock(guardedOperation));
    operationTail = result.then(() => undefined, () => undefined);
    return result;
  }

  function transition<T>(intent: SessionIntent, operation: () => Promise<T>): Promise<SessionIntentResult<T>> {
    const guardedOperation = async (): Promise<SessionIntentResult<T>> => {
      if (!transitionIsCurrent(intent)) return { committed: false };
      const value = await operation();
      // Once a transition starts, its cookie and local-state effects are real. A newer
      // transition is queued behind it and owns the final state, but cannot erase that fact.
      return { committed: true, value };
    };
    const result = operationTail.then(() => withTransitionLock(guardedOperation));
    const completed = result.finally(() => { pendingTransitions.delete(intent.id); });
    operationTail = completed.then(() => undefined, () => undefined);
    return completed;
  }

  return {
    beginObservation: () => {
      nextIntentId += 1;
      latestObservationId = nextIntentId;
      return {
        kind: "observation",
        id: nextIntentId,
        transitionEpoch,
        beganDuringTransition: pendingTransitions.size > 0,
      };
    },
    beginTransition: () => {
      nextIntentId += 1;
      transitionEpoch += 1;
      latestTransitionId = nextIntentId;
      pendingTransitions.add(nextIntentId);
      return {
        kind: "transition",
        id: nextIntentId,
        transitionEpoch,
        beganDuringTransition: false,
      };
    },
    isCurrent: (intent) => intent.kind === "observation" ? observationIsCurrent(intent) : transitionIsCurrent(intent),
    observe,
    transition,
  };
}

export async function withSameOriginSessionTransitionLock<T>(operation: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request(SESSION_TRANSITION_LOCK_NAME, { mode: "exclusive" }, operation);
  }
  return operation();
}

export async function invalidateAuthenticatedState({
  invalidateSession,
  clearOffline,
  broadcast,
}: {
  invalidateSession: () => void | Promise<unknown>;
  clearOffline: () => Promise<void>;
  broadcast?: () => void;
}): Promise<{ cleanupFailed: boolean }> {
  await invalidateSession();
  try { broadcast?.(); } catch { /* Other tabs will still fail closed on their next request. */ }
  try {
    await clearOffline();
    return { cleanupFailed: false };
  } catch {
    return { cleanupFailed: true };
  }
}

export async function signOutAuthenticatedState({
  invalidateSession,
  clearOffline,
  broadcast,
  revokeRemote,
}: {
  invalidateSession: () => void | Promise<unknown>;
  clearOffline: () => Promise<void>;
  broadcast?: () => void;
  revokeRemote: () => Promise<void>;
}): Promise<{ cleanupFailed: boolean; revocationFailed: boolean }> {
  const { cleanupFailed } = await invalidateAuthenticatedState({ invalidateSession, clearOffline, broadcast });
  try {
    await revokeRemote();
    return { cleanupFailed, revocationFailed: false };
  } catch {
    return { cleanupFailed, revocationFailed: true };
  }
}
